import React, { useState, useEffect } from 'react';
import { CodelcoLayout } from '../../components/codelco/CodelcoLayout';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Bell, AlertTriangle, CheckCheck, Trash2, RefreshCw, Filter, MailCheck, Inbox } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CodelcoNotificacionesPage() {
    const [items, setItems] = useState([]);
    const [noLeidas, setNoLeidas] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const load = async () => {
        setLoading(true);
        try {
            const q = filter === 'no_leidas' ? '?solo_no_leidas=true' : '';
            const r = await fetch(`${API_URL}/api/codelco/notificaciones${q}`, { headers });
            if (r.ok) {
                const d = await r.json();
                setItems(d.items || []);
                setNoLeidas(d.no_leidas || 0);
            }
        } catch { /* ignore */ }
        setLoading(false);
    };

    const marcarLeida = async (id) => {
        try {
            await fetch(`${API_URL}/api/codelco/notificaciones/${id}/leer`, { method: 'POST', headers });
            load();
        } catch { /* ignore */ }
    };

    const marcarTodas = async () => {
        try {
            const r = await fetch(`${API_URL}/api/codelco/notificaciones/marcar-todas`, { method: 'POST', headers });
            if (r.ok) {
                const d = await r.json();
                toast.success(`${d.actualizadas} marcadas como leídas`);
                load();
            }
        } catch { /* ignore */ }
    };

    const borrar = async (id) => {
        try {
            await fetch(`${API_URL}/api/codelco/notificaciones/${id}`, { method: 'DELETE', headers });
            load();
        } catch { /* ignore */ }
    };

    return (
        <CodelcoLayout>
            <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
                            <Bell className="h-7 w-7 text-amber-500" />
                            Notificaciones
                            {noLeidas > 0 && <Badge className="bg-red-500 text-white animate-pulse">{noLeidas} sin leer</Badge>}
                        </h1>
                        <p className="text-muted-foreground">Alertas por errores consecutivos del integrador</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={load} data-testid="btn-refresh-notif">
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Refrescar
                        </Button>
                        {noLeidas > 0 && (
                            <Button onClick={marcarTodas} variant="outline" data-testid="btn-marcar-todas">
                                <MailCheck className="h-4 w-4 mr-2" /> Marcar todas
                            </Button>
                        )}
                    </div>
                </div>

                <Card className="border-2">
                    <CardHeader className="pb-3">
                        <div className="flex gap-2 items-center">
                            <Filter className="h-4 w-4 text-muted-foreground" />
                            {[
                                { v: 'all', l: 'Todas' },
                                { v: 'no_leidas', l: 'No leídas' },
                            ].map(f => (
                                <Button key={f.v} variant={filter === f.v ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f.v)} data-testid={`filter-${f.v}`}>
                                    {f.l}
                                </Button>
                            ))}
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="p-8 text-center text-muted-foreground">Cargando...</div>
                        ) : items.length === 0 ? (
                            <div className="p-12 text-center text-muted-foreground">
                                <Inbox className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                <p>Sin notificaciones</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {items.map(n => (
                                    <div key={n.id} className={`p-4 border-2 rounded-xl flex items-start gap-3 transition-colors ${n.leida ? 'border-border opacity-70' : 'border-amber-500/40 bg-amber-500/5'}`} data-testid={`notif-${n.id}`}>
                                        <AlertTriangle className={`h-5 w-5 mt-1 flex-shrink-0 ${n.leida ? 'text-muted-foreground' : 'text-amber-500'}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-bold">{n.titulo}</span>
                                                {n.errores_consecutivos && <Badge variant="destructive" className="text-xs">{n.errores_consecutivos} errores</Badge>}
                                                {!n.leida && <Badge className="bg-amber-500 text-white text-xs">Nueva</Badge>}
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-1">{n.mensaje}</p>
                                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                                <span>{new Date(n.created_at).toLocaleString()}</span>
                                                {n.canales_enviados?.length > 0 && (
                                                    <span className="flex items-center gap-1">
                                                        Enviado por: {n.canales_enviados.join(', ')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            {!n.leida && (
                                                <Button variant="ghost" size="icon" onClick={() => marcarLeida(n.id)} title="Marcar leída" data-testid={`btn-leer-${n.id}`}>
                                                    <CheckCheck className="h-4 w-4" />
                                                </Button>
                                            )}
                                            <Button variant="ghost" size="icon" onClick={() => borrar(n.id)} title="Borrar">
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </CodelcoLayout>
    );
}
